import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useTypedSelector } from "@/config/configureAppStore";
import { clientProjectApi } from "../api";
import {
  putInitialState,
  setUploadedSampleFileIds
} from "@/reducers/slices/clientProjectPopupSlice/clientProjectPopupSlice";
import { CREATE_PROJECT_STEPS } from "../constants";
import CreateProjectSkeleton from "./CreateProject.skeleton";
import { projectDetailsApi } from "@/features/clientProjectDetails/api";
import { CreateProjectFlow, CreateProjectLayout } from "..";
import {
  shouldShowCreditsPromptPolicy,
  usePermissions
} from "@/features/permissions";
import { setCreditsDialog } from "@/reducers/slices/dialogSlice/dialogSlice";

function CreateProject(): React.ReactNode {
  const projectId = useTypedSelector(
    (state) => state.clientProjectPopup?.projectId
  );
  const {
    projectName,
    numberOfVideos = 0,
    sourceFileResponseList
  } = useTypedSelector((state) => state.clientProjectPopup || {});
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { canAccess } = usePermissions();
  const [isLoading, setIsLoading] = useState(false);
  const isLoadingProjectRef = useRef(false);

  const shouldShowCreditsPrompt = canAccess({
    policy: shouldShowCreditsPromptPolicy,
    policyArgs: null
  });

  const fetchInitialState = async () => {
    if (!projectId || isLoadingProjectRef.current) {
      return;
    }
    setIsLoading(true);
    isLoadingProjectRef.current = true;
    const res = await clientProjectApi
      .getById({
        projectId
      })
      .catch((e) => {
        setIsLoading(false);
        isLoadingProjectRef.current = false;
        console.error("Error fetching project details", e);
      });

    if (res) {
      const data = res.data.responseData;
      const payload = data.project;
      const draftState: keyof typeof CREATE_PROJECT_STEPS = payload.draftState;
      payload.currentStep =
        CREATE_PROJECT_STEPS[draftState]?.int ||
        CREATE_PROJECT_STEPS["UPLOAD_FILES"]?.int;
      payload.sourceFileResponseList = {};
      const sourceFileRes = await projectDetailsApi.fetchSourceFiles({
        projectId
      });
      sourceFileRes.data.responseData.map((file: { sourceFileId: string }) => {
        payload.sourceFileResponseList[file.sourceFileId] = file;
      });
      Promise.all([
        projectDetailsApi.fetchDubStatus({
          projectId,
          fileIds: Object.keys(payload.sourceFileResponseList)
        }),
        projectDetailsApi.fetchSourceFileStatus({
          projectId,
          fileIds: Object.keys(payload.sourceFileResponseList)
        })
      ]).then((res) => {
        console.log("res", res);
        dispatch(putInitialState(payload));
        const sampleFileIds = sourceFileRes.data.responseData
          .map((f: any) => f.sourceSampleFileId)
          .filter(Boolean);
        dispatch(setUploadedSampleFileIds(sampleFileIds));
        setIsLoading(false);
        isLoadingProjectRef.current = false;
      });
    }
  };

  useEffect(() => {
    if (
      !isLoadingProjectRef.current &&
      projectId &&
      (!projectName?.length || (numberOfVideos > 0 && !sourceFileResponseList))
    ) {
      fetchInitialState();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  useEffect(() => {
    if (shouldShowCreditsPrompt) {
      dispatch(setCreditsDialog(true));
      navigate("/home");
    }
  }, [shouldShowCreditsPrompt, dispatch, navigate]);

  if (projectId && isLoading) {
    return <CreateProjectSkeleton />;
  }

  return (
    <>
      <CreateProjectLayout>
        <CreateProjectFlow />
      </CreateProjectLayout>
    </>
  );
}

export default CreateProject;
